export type CellKind =
  | 'text'
  | 'number'
  | 'currency'
  | 'boolean'
  | 'url'
  | 'date'
  | 'enum'
  | 'array'

export interface ColumnSchema {
  key: string
  kind: CellKind
  currency?: string
  enumValues?: string[]
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:\d{2})?)?$/
const CURRENCY_PREFIX_RE = /^[$€£¥₹][\d,]+/
const CURRENCY_SUFFIX_RE = /[\d,]+[$€£¥₹]$/
const URL_RE = /^https?:\/\//i
const CURRENCY_COL_RE = /price|revenue|amount|cost|fee|salary|total|earning/i
const DATE_COL_RE = /date|time|created|updated|at$|_on$/i
const YEAR_COL_RE = /^(year|yr|model_year|manufacture_year)$/i
const BOOL_TRUTHY = new Set(['true', 'false', '1', '0', 'yes', 'no', 'y', 'n'])

function sampleValues(rows: Record<string, unknown>[], key: string, n = 50): unknown[] {
  const out: unknown[] = []
  for (let i = 0; i < Math.min(rows.length, n); i++) {
    const row = rows[i]
    if (!row) continue
    const v = row[key]
    if (v !== null && v !== undefined && v !== '') out.push(v)
  }
  return out
}

function inferKind(key: string, samples: unknown[]): CellKind {
  if (samples.length === 0) return 'text'

  const nonNull = samples.filter(v => v !== null && v !== undefined)
  if (nonNull.length === 0) return 'text'

  if (nonNull.every(v => Array.isArray(v))) return 'array'

  if (nonNull.every(v => typeof v === 'boolean')) return 'boolean'
  const strLower = nonNull.map(v => String(v).toLowerCase().trim())
  if (nonNull.every(v => typeof v === 'boolean' || BOOL_TRUTHY.has(String(v).toLowerCase()))) {
    const unique = new Set(strLower)
    if (unique.size <= 4) return 'boolean'
  }

  if (nonNull.some(v => URL_RE.test(String(v)))) {
    const urlFrac = nonNull.filter(v => URL_RE.test(String(v))).length / nonNull.length
    if (urlFrac > 0.4) return 'url'
  }

  if (nonNull.every(v => ISO_DATE_RE.test(String(v))) || DATE_COL_RE.test(key)) {
    if (nonNull.filter(v => ISO_DATE_RE.test(String(v))).length / nonNull.length > 0.7) return 'date'
  }

  if (nonNull.some(v => CURRENCY_PREFIX_RE.test(String(v)) || CURRENCY_SUFFIX_RE.test(String(v)))) {
    return 'currency'
  }

  if (nonNull.every(v => typeof v === 'number' || (!isNaN(Number(v)) && String(v).trim() !== ''))) {
    if (YEAR_COL_RE.test(key) && nonNull.every(v => { const n = Number(v); return Number.isInteger(n) && n >= 1900 && n <= 2100 })) return 'enum'
    if (CURRENCY_COL_RE.test(key)) return 'currency'
    return 'number'
  }

  const unique = new Set(nonNull.map(v => String(v)))
  const enumRatio = unique.size / nonNull.length
  const isEnum = unique.size <= 6 || (unique.size <= 20 && enumRatio <= 0.05)
  if (isEnum && nonNull.length >= 10) return 'enum'

  return 'text'
}

export function inferSchema(rows: Record<string, unknown>[]): ColumnSchema[] {
  if (rows.length === 0) return []
  const keys = [...new Set(rows.flatMap(Object.keys))].filter(k => k !== '__rowIdx')
  return keys.map(key => {
    const samples = sampleValues(rows, key)
    const kind = inferKind(key, samples)
    const schema: ColumnSchema = { key, kind }
    if (kind === 'enum') {
      schema.enumValues = [...new Set(samples.map(v => String(v)))]
    }
    if (kind === 'currency') {
      const hasDollar = samples.some(v => String(v).includes('$'))
      schema.currency = hasDollar ? 'USD' : undefined
    }
    return schema
  })
}
